import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useIntersect } from '../hooks/useIntersect';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

// ── Article Card ──────────────────────────────────────────
function ArticleCard({ article, featured = false, idx = 0 }) {
  const [ref, visible] = useIntersect();
  const [hover, setHover] = useState(false);
  const imgUrl   = resolveMedia(article.cover_image_url || article.thumbnail_url || article.media_url);
  const hasVideo = !!article.video_url;

  return (
    <Link ref={ref} to={`/news/${article.slug}`}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className="card" style={{
        padding:0, display:'flex', flexDirection:'column',
        opacity: visible?1:0, transform: visible?'none':'translateY(20px)',
        transition:`opacity 0.5s ${idx*0.07}s ease, transform 0.5s ${idx*0.07}s ease, border-color 0.2s`,
        overflow:'hidden', textDecoration:'none',
        borderColor: hover?'rgba(232,164,58,0.4)':undefined,
      }}>
      {imgUrl && (
        <div style={{ height:featured?260:170, overflow:'hidden', position:'relative', flexShrink:0 }}>
          <img src={imgUrl} alt={article.title}
            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.5s ease', transform:hover?'scale(1.04)':'scale(1)' }}
            onError={e => { e.target.parentNode.style.display='none'; }}/>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(8,10,13,0.6) 0%, transparent 50%)' }}/>
          {hasVideo && <div style={{ position:'absolute', bottom:10, right:10, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', borderRadius:4, padding:'3px 8px', fontSize:10, color:'#fff', fontWeight:600 }}>▶ Video</div>}
          <div style={{ position:'absolute', top:10, left:10 }}>
            <span className="badge badge-gold" style={{ fontSize:'0.6rem', background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', textTransform:'capitalize' }}>{article.content_type}</span>
          </div>
        </div>
      )}
      <div style={{ padding:featured?'24px 28px 28px':'18px 22px 22px', display:'flex', flexDirection:'column', gap:10, flex:1 }}>
        {!imgUrl && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <span className="badge badge-gold" style={{ fontSize:'0.6rem', textTransform:'capitalize' }}>{article.content_type}</span>
            {article.tags?.slice(0,2).map(t => <span key={t} className="badge badge-dim" style={{ fontSize:'0.6rem' }}>{t}</span>)}
          </div>
        )}
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:featured?'1.35rem':'1rem', fontWeight:600, lineHeight:1.3, flex:1, color:hover?'var(--gold)':'var(--t-primary)', transition:'color 0.15s' }}>
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="body-sm" style={{ color:'var(--t-secondary)', lineHeight:1.65 }}>
            {article.excerpt.slice(0, featured?180:100)}{article.excerpt.length>(featured?180:100)?'…':''}
          </p>
        )}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:8, borderTop:'1px solid var(--border)', marginTop:'auto' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>{article.author_name||'MilleHills'}</span>
            {article.published_at && <span style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>· {new Date(article.published_at).toLocaleDateString('en-RW',{month:'short',day:'numeric',year:'numeric'})}</span>}
          </div>
          <span style={{ color:'var(--gold)', fontSize:'0.85rem', transition:'transform 0.15s', transform:hover?'translateX(3px)':'none' }}>Read →</span>
        </div>
      </div>
    </Link>
  );
}

// ── Media Gallery with Lightbox ───────────────────────────
function MediaGallery({ urls }) {
  const [lightbox, setLightbox] = useState(null);
  if (!urls || urls.length === 0) return null;
  const cols = urls.length===1?'1fr':urls.length===2?'1fr 1fr':'repeat(3,1fr)';

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:cols, gap:8, marginBottom:32 }}>
        {urls.map((url,i) => (
          <div key={i} onClick={() => setLightbox(i)}
            style={{ aspectRatio:'16/10', overflow:'hidden', borderRadius:8, cursor:'zoom-in', position:'relative', background:'var(--bg-2)' }}>
            <img src={url} alt={`Media ${i+1}`}
              style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.3s' }}
              onMouseEnter={e => e.target.style.transform='scale(1.04)'}
              onMouseLeave={e => e.target.style.transform='scale(1)'}
              onError={e => e.target.parentNode.style.display='none'}/>
            {urls.length>1 && <div style={{ position:'absolute', bottom:6, right:8, background:'rgba(0,0,0,0.55)', borderRadius:4, padding:'2px 6px', fontSize:10, color:'#fff' }}>{i+1}/{urls.length}</div>}
          </div>
        ))}
      </div>

      {lightbox !== null && (
        <div onClick={() => setLightbox(null)} style={{ position:'fixed', inset:0, zIndex:900, background:'rgba(0,0,0,0.96)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          {lightbox > 0 && <button onClick={e=>{e.stopPropagation();setLightbox(l=>l-1);}} style={{ position:'absolute', left:20, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', fontSize:28, width:48, height:48, borderRadius:'50%', cursor:'pointer' }}>‹</button>}
          <img src={urls[lightbox]} alt="" style={{ maxWidth:'90vw', maxHeight:'90vh', objectFit:'contain', borderRadius:8 }}/>
          {lightbox < urls.length-1 && <button onClick={e=>{e.stopPropagation();setLightbox(l=>l+1);}} style={{ position:'absolute', right:20, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', fontSize:28, width:48, height:48, borderRadius:'50%', cursor:'pointer' }}>›</button>}
          <button onClick={()=>setLightbox(null)} style={{ position:'absolute', top:16, right:16, background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', fontSize:18, width:36, height:36, borderRadius:'50%', cursor:'pointer' }}>✕</button>
          {urls.length > 1 && (
            <div style={{ position:'absolute', bottom:20, left:'50%', transform:'translateX(-50%)', display:'flex', gap:6 }}>
              {urls.map((_,i) => <div key={i} onClick={e=>{e.stopPropagation();setLightbox(i);}} style={{ width:8, height:8, borderRadius:'50%', background:i===lightbox?'var(--gold)':'rgba(255,255,255,0.3)', cursor:'pointer', transition:'background 0.2s' }}/>)}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── News List ─────────────────────────────────────────────
export function NewsListPage() {
  const [articles,setArticles] = useState([]);
  const [loading,setLoading]   = useState(true);
  const [page,setPage]         = useState(1);
  const [pagination,setPag]    = useState(null);
  const [type,setType]         = useState('');

  useEffect(() => {
    setLoading(true);
    publicAPI.articles({ page, limit:9, content_type:type||undefined })
      .then(r => { setArticles(r.data?.data||[]); setPag(r.data?.pagination); })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [page, type]);

  return (
    <div>
      <section style={{ paddingTop:'calc(var(--nav-h) + clamp(60px,8vw,100px))', paddingBottom:'clamp(60px,8vw,100px)', background:'linear-gradient(180deg, var(--bg-1) 0%, var(--bg-0) 100%)' }}>
        <div className="container">
          <div className="section-label label" style={{ marginBottom:20 }}>News & Insights</div>
          <h1 className="display-lg" style={{ marginBottom:16 }}>Stories from the<br/><span style={{ color:'var(--gold)' }}>Ecosystem</span></h1>
          <p className="body-lg" style={{ color:'var(--t-secondary)', maxWidth:500 }}>Updates, announcements, and insights from MilleHills and the wider digital ecosystem in Rwanda.</p>
        </div>
      </section>

      <div style={{ background:'var(--bg-0)', borderBottom:'1px solid var(--border)', position:'sticky', top:'var(--nav-h)', zIndex:50 }}>
        <div className="container" style={{ padding:'14px clamp(20px,5vw,60px)', display:'flex', gap:8, flexWrap:'wrap' }}>
          {['','media','corporate','program','marketing'].map(t => (
            <button key={t} onClick={() => { setType(t); setPage(1); }} style={{
              padding:'7px 14px', borderRadius:20, fontSize:'0.78rem', cursor:'pointer', textTransform:'capitalize',
              fontWeight:type===t?700:400, background:type===t?'var(--gold)':'var(--bg-2)',
              color:type===t?'#080a0d':'var(--t-secondary)',
              border:`1px solid ${type===t?'var(--gold)':'var(--border)'}`, transition:'all 0.15s',
            }}>{t||'All'}</button>
          ))}
        </div>
      </div>

      <section style={{ padding:'clamp(40px,6vw,80px) 0', minHeight:'50vh' }}>
        <div className="container">
          {loading ? (
            <div style={{ textAlign:'center', padding:60, color:'var(--t-muted)' }}>Loading…</div>
          ) : articles.length===0 ? (
            <div style={{ textAlign:'center', padding:60, color:'var(--t-muted)' }}>No articles found</div>
          ) : (
            <>
              {page===1 && articles[0] && <div style={{ marginBottom:28 }}><ArticleCard article={articles[0]} featured idx={0}/></div>}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:20 }}>
                {(page===1?articles.slice(1):articles).map((a,i) => <ArticleCard key={a.id} article={a} idx={i}/>)}
              </div>
            </>
          )}
          {pagination?.pages>1 && (
            <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:48 }}>
              {[...Array(pagination.pages)].map((_,i) => (
                <button key={i} onClick={() => setPage(i+1)} style={{ width:36, height:36, borderRadius:'var(--r-sm)', background:page===i+1?'var(--gold)':'var(--bg-2)', color:page===i+1?'#080a0d':'var(--t-secondary)', border:`1px solid ${page===i+1?'var(--gold)':'var(--border)'}`, fontWeight:page===i+1?700:400, cursor:'pointer', fontSize:'0.875rem' }}>{i+1}</button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ── Article Detail ────────────────────────────────────────
export function ArticleDetailPage() {
  const { slug } = useParams();
  const [article,setArticle] = useState(null);
  const [related,setRelated] = useState([]);
  const [loading,setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    publicAPI.article(slug)
      .then(r => {
        const a = r.data?.data;
        setArticle(a);
        if (a?.content_type) {
          publicAPI.articles({ limit:4, content_type:a.content_type })
            .then(res => setRelated((res.data?.data||[]).filter(x=>x.slug!==slug).slice(0,3)))
            .catch(()=>{});
        }
      })
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ paddingTop:'calc(var(--nav-h) + 80px)', minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--t-muted)' }}>Loading…</div>
  );
  if (!article) return (
    <div style={{ paddingTop:'calc(var(--nav-h) + 80px)', minHeight:'60vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
      <div style={{ fontSize:'3rem', opacity:0.2 }}>◎</div>
      <div style={{ color:'var(--t-muted)' }}>Article not found.</div>
      <Link to="/news" className="btn btn-outline">Back to News →</Link>
    </div>
  );

  const heroUrl  = resolveMedia(article.cover_image_url || article.thumbnail_url || article.media_url);
  const videoUrl = resolveMedia(article.video_url);
  const extraMedia = (article.media_urls||[]).map(resolveMedia).filter(u=>u&&u!==heroUrl);
  const isHTML = article.content?.trim().startsWith('<');

  return (
    <div>
      {heroUrl && (
        <div style={{ height:'clamp(240px,40vw,480px)', position:'relative', overflow:'hidden', marginTop:'var(--nav-h)' }}>
          <img src={heroUrl} alt={article.title} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} onError={e=>e.target.parentNode.style.display='none'}/>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, var(--bg-0) 0%, rgba(8,10,13,0.3) 60%, transparent 100%)' }}/>
        </div>
      )}

      <section style={{ paddingTop:heroUrl?0:'calc(var(--nav-h) + 60px)', marginTop:heroUrl?'-80px':0, paddingBottom:48, background:'var(--bg-0)', position:'relative', zIndex:1, borderBottom:'1px solid var(--border)' }}>
        <div className="container" style={{ maxWidth:800 }}>
          <Link to="/news" style={{ color:'var(--t-muted)', fontSize:'0.8rem', marginBottom:24, display:'inline-flex', alignItems:'center', gap:6, textDecoration:'none' }}>← Back to News</Link>
          <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', marginTop:16 }}>
            <span className="badge badge-gold" style={{ textTransform:'capitalize' }}>{article.content_type}</span>
            {article.tags?.map(t => <span key={t} className="badge badge-dim">{t}</span>)}
          </div>
          <h1 className="display-lg" style={{ marginBottom:20, lineHeight:1.05 }}>{article.title}</h1>
          {article.excerpt && <p className="body-lg" style={{ color:'var(--t-secondary)', lineHeight:1.75 }}>{article.excerpt}</p>}
          <div style={{ display:'flex', gap:16, marginTop:24, fontSize:'0.8rem', color:'var(--t-muted)', flexWrap:'wrap' }}>
            {article.author_name && <span>By <strong style={{ color:'var(--t-secondary)' }}>{article.author_name}</strong></span>}
            {article.published_at && <span>{new Date(article.published_at).toLocaleDateString('en-RW',{year:'numeric',month:'long',day:'numeric'})}</span>}
          </div>
        </div>
      </section>

      <section style={{ padding:'clamp(40px,6vw,80px) 0' }}>
        <div className="container" style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:60, alignItems:'start' }}>

          <div>
            {videoUrl && (
              <div style={{ marginBottom:32, borderRadius:12, overflow:'hidden', background:'#000', aspectRatio:'16/9' }}>
                <video controls poster={heroUrl||undefined} style={{ width:'100%', height:'100%', display:'block' }}>
                  <source src={videoUrl}/>
                </video>
              </div>
            )}

            <MediaGallery urls={extraMedia}/>

            {isHTML ? (
              <div dangerouslySetInnerHTML={{ __html:article.content }} className="article-content"/>
            ) : (
              <div style={{ fontSize:'1.05rem', lineHeight:1.85, color:'var(--t-secondary)', whiteSpace:'pre-wrap' }}>{article.content}</div>
            )}

            {article.tags?.length>0 && (
              <div style={{ marginTop:48, paddingTop:24, borderTop:'1px solid var(--border)', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontSize:'0.8rem', color:'var(--t-muted)' }}>Tags:</span>
                {article.tags.map(t => <span key={t} className="badge badge-dim">{t}</span>)}
              </div>
            )}

            {/* Share */}
            <div style={{ marginTop:32, paddingTop:24, borderTop:'1px solid var(--border)', display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ fontSize:'0.82rem', color:'var(--t-muted)' }}>Share:</span>
              {[
                { label:'X / Twitter', url:`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}` },
                { label:'LinkedIn',    url:`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}` },
                { label:'WhatsApp',    url:`https://wa.me/?text=${encodeURIComponent(article.title+' '+window.location.href)}` },
              ].map(s => (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize:'0.8rem', padding:'6px 12px', borderRadius:6, background:'var(--bg-2)', border:'1px solid var(--border)', color:'var(--t-secondary)', textDecoration:'none', transition:'all 0.15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(232,164,58,0.4)';e.currentTarget.style.color='var(--gold)';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--t-secondary)';}}>
                  {s.label}
                </a>
              ))}
            </div>

            <div style={{ marginTop:32 }}>
              <Link to="/news" className="btn btn-outline">← Back to News</Link>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ position:'sticky', top:'calc(var(--nav-h) + 24px)', display:'flex', flexDirection:'column', gap:20 }}>
            <div style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:18 }}>
              <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.9rem', marginBottom:14 }}>About This Article</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {[
                  ['Author',    article.author_name||'MilleHills'],
                  ['Published', article.published_at?new Date(article.published_at).toLocaleDateString('en-RW',{day:'numeric',month:'short',year:'numeric'}):'—'],
                  ['Category',  article.content_type],
                ].map(([k,v]) => (
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem' }}>
                    <span style={{ color:'var(--t-muted)' }}>{k}</span>
                    <span style={{ fontWeight:500, textTransform:'capitalize' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {related.length>0 && (
              <div style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', overflow:'hidden' }}>
                <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.9rem' }}>Related Articles</div>
                {related.map(r => (
                  <Link key={r.id} to={`/news/${r.slug}`}
                    style={{ display:'flex', gap:12, padding:'12px 16px', borderBottom:'1px solid var(--border-subtle)', textDecoration:'none', transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    {resolveMedia(r.cover_image_url||r.media_url||r.thumbnail_url) && (
                      <div style={{ width:52, height:52, borderRadius:6, overflow:'hidden', flexShrink:0 }}>
                        <img src={resolveMedia(r.cover_image_url||r.media_url||r.thumbnail_url)} alt={r.title} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.parentNode.style.display='none'}/>
                      </div>
                    )}
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:'0.82rem', fontWeight:500, color:'var(--t-primary)', lineHeight:1.4, marginBottom:4 }}>{r.title}</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>{r.published_at?new Date(r.published_at).toLocaleDateString('en-RW',{month:'short',day:'numeric'}):''}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div style={{ background:'rgba(232,164,58,0.06)', border:'1px solid rgba(232,164,58,0.2)', borderRadius:'var(--r-lg)', padding:18, textAlign:'center' }}>
              <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.9rem', marginBottom:8 }}>Stay Updated</div>
              <p style={{ fontSize:'0.8rem', color:'var(--t-muted)', marginBottom:14, lineHeight:1.6 }}>Get the latest from MilleHills.</p>
              <Link to="/contact" className="btn btn-primary" style={{ width:'100%', justifyContent:'center', display:'flex', fontSize:'0.82rem' }}>Contact Us →</Link>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .article-content { font-size:1.05rem; line-height:1.85; color:var(--t-secondary); }
        .article-content h2 { font-family:var(--font-display); font-size:1.4rem; font-weight:700; color:var(--t-primary); margin:2rem 0 1rem; }
        .article-content h3 { font-family:var(--font-display); font-size:1.1rem; font-weight:600; color:var(--t-primary); margin:1.5rem 0 0.75rem; }
        .article-content p  { margin:0 0 1.2rem; }
        .article-content ul,.article-content ol { padding-left:1.5rem; margin:0 0 1.2rem; display:flex; flex-direction:column; gap:6px; }
        .article-content li { color:var(--t-secondary); line-height:1.7; }
        .article-content strong { color:var(--t-primary); font-weight:600; }
        .article-content a  { color:var(--gold); text-decoration:none; }
        .article-content a:hover { text-decoration:underline; }
        .article-content blockquote { border-left:3px solid var(--gold); padding:12px 20px; margin:1.5rem 0; background:rgba(232,164,58,0.05); border-radius:0 8px 8px 0; font-style:italic; color:var(--t-secondary); }
        .article-content img { max-width:100%; border-radius:8px; margin:1rem 0; cursor:pointer; }
        .article-content code { background:var(--bg-2); padding:2px 6px; border-radius:4px; font-family:monospace; font-size:0.9em; color:var(--gold); }
        .article-content pre { background:var(--bg-2); padding:16px; border-radius:8px; overflow-x:auto; margin:1rem 0; }
      `}</style>
    </div>
  );
}